// src/Stores/LearnerStore/LearningsStore.ts
import { create } from 'zustand';
import {
  getMyCourses,
  getWishlistCourses,
  getBrowseCourses,
  getCourseDetails,
  saveCourse,
  unsaveCourse,
  enrollInCourse,
  markLessonComplete,
  Course,
  CourseDetails,
} from '@/services/LearnerService/LearningsService';

interface LearningsState {
  // Data
  myCourses: Course[] | null;
  wishlistCourses: Course[] | null;
  browseCourses: Course[] | null;
  courseDetails: CourseDetails | null;
  savedCourseIds: Set<number>;

  // Loading states
  loadingMyCourses: boolean;
  loadingWishlist: boolean;
  loadingBrowse: boolean;
  loadingCourseDetails: boolean;
  loadingSavedStatus: boolean;

  // Error states
  errorMyCourses: string | null;
  errorWishlist: string | null;
  errorBrowse: string | null;
  errorCourseDetails: string | null;

  // Actions
  fetchMyCourses: () => Promise<void>;
  fetchWishlistCourses: () => Promise<void>;
  fetchBrowseCourses: () => Promise<void>;
  fetchCourseDetails: (courseId: number) => Promise<void>;
  fetchSavedCourseIds: () => Promise<void>;
  toggleSaveCourse: (courseId: number) => Promise<void>;
  enrollInCourse: (courseId: number) => Promise<void>;
  markLessonComplete: (lessonId: number, courseId: number) => Promise<void>;
}

export const useLearningsStore = create<LearningsState>((set, get) => ({
  // Initial state
  myCourses: null,
  wishlistCourses: null,
  browseCourses: null,
  courseDetails: null,
  savedCourseIds: new Set<number>(),

  loadingMyCourses: false,
  loadingWishlist: false,
  loadingBrowse: false,
  loadingCourseDetails: false,
  loadingSavedStatus: false,

  errorMyCourses: null,
  errorWishlist: null,
  errorBrowse: null,
  errorCourseDetails: null,

  // Fetch My Courses
  fetchMyCourses: async () => {
    set({ loadingMyCourses: true, errorMyCourses: null });

    try {
      const res = await getMyCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load my courses');
      }

      set({
        myCourses: res.data ?? [],
        loadingMyCourses: false,
      });
    } catch (err: any) {
      set({
        errorMyCourses: err.message ?? 'Something went wrong',
        loadingMyCourses: false,
      });
    }
  },

  // Fetch Wishlist Courses
  fetchWishlistCourses: async () => {
    set({ loadingWishlist: true, errorWishlist: null });

    try {
      const res = await getWishlistCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load wishlist courses');
      }

      set({
        wishlistCourses: res.data ?? [],
        loadingWishlist: false,
      });
    } catch (err: any) {
      set({
        errorWishlist: err.message ?? 'Something went wrong',
        loadingWishlist: false,
      });
    }
  },

  // Fetch Browse Courses
  fetchBrowseCourses: async () => {
    set({ loadingBrowse: true, errorBrowse: null });

    try {
      const res = await getBrowseCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load browse courses');
      }

      set({
        browseCourses: res.data ?? [],
        loadingBrowse: false,
      });
    } catch (err: any) {
      set({
        errorBrowse: err.message ?? 'Something went wrong',
        loadingBrowse: false,
      });
    }
  },

  // Fetch Course Details
  fetchCourseDetails: async (courseId: number) => {
    set({ loadingCourseDetails: true, errorCourseDetails: null });

    try {
      const res = await getCourseDetails(courseId);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load course details');
      }

      set({
        courseDetails: res.data ?? null,
        loadingCourseDetails: false,
      });
    } catch (err: any) {
      set({
        errorCourseDetails: err.message ?? 'Something went wrong',
        loadingCourseDetails: false,
      });
    }
  },

  // Fetch Saved Course IDs
  // Fetches all saved courses and extracts their IDs to populate savedCourseIds Set
  fetchSavedCourseIds: async () => {
    set({ loadingSavedStatus: true });

    try {
      const res = await getWishlistCourses();

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load saved courses');
      }

      const ids = new Set((res.data ?? []).map(course => course.id));
      set({
        savedCourseIds: ids,
        loadingSavedStatus: false,
      });
    } catch (err: any) {
      set({
        loadingSavedStatus: false,
      });
      console.error('Failed to fetch saved course IDs:', err.message);
    }
  },

  // Toggle Save Course (Optimistic Update)
  // Immediately updates UI, then calls API, rolls back on error
  toggleSaveCourse: async (courseId: number) => {
    const { savedCourseIds, wishlistCourses } = get();
    const wasSaved = savedCourseIds.has(courseId);

    // Create new set for optimistic update
    const newSavedIds = new Set(savedCourseIds);
    if (wasSaved) {
      newSavedIds.delete(courseId);
    } else {
      newSavedIds.add(courseId);
    }

    // Optimistic update - immediately update UI
    set({ savedCourseIds: newSavedIds });

    try {
      // Call API
      const res = wasSaved
        ? await unsaveCourse(courseId)
        : await saveCourse(courseId);

      if (!res.success) {
        // Rollback on error
        set({ savedCourseIds });
        throw new Error(res.error?.message ?? 'Failed to toggle save status');
      }

      // Refresh wishlist if currently viewing it
      if (wishlistCourses !== null) {
        const listRes = await getWishlistCourses();
        if (listRes.success) {
          set({ wishlistCourses: listRes.data ?? [] });
        }
      }
    } catch (err: any) {
      // Rollback on error
      set({ savedCourseIds });
      throw err;
    }
  },

  // Enroll in Course
  // Enrolls user and refreshes my courses list
  enrollInCourse: async (courseId: number) => {
    set({ loadingBrowse: true, errorBrowse: null });

    try {
      const res = await enrollInCourse(courseId);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to enroll in course');
      }

      // Refresh my courses to include newly enrolled course
      await get().fetchMyCourses();

      // Also refresh browse courses to update is_enrolled status
      // This will cause the enrolled course to disappear from the browse list
      await get().fetchBrowseCourses();

      set({ loadingBrowse: false });
    } catch (err: any) {
      set({
        errorBrowse: err.message ?? 'Something went wrong',
        loadingBrowse: false,
      });
      throw err;
    }
  },

  // Mark Lesson Complete
  // Marks a lesson as completed by calling the API
  markLessonComplete: async (lessonId: number, courseId: number) => {
    try {
      const res = await markLessonComplete(lessonId, courseId);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to mark lesson complete');
      }

      // Optionally refresh course details to update lesson completion status
      // This is useful to see the updated lesson state in the UI
      const { courseDetails } = get();
      if (courseDetails && courseDetails.id === courseId) {
        await get().fetchCourseDetails(courseId);
      }
    } catch (err: any) {
      console.error('Failed to mark lesson complete:', err.message);
      throw err;
    }
  },
}));
